<docs>
A selectable group of pills, single or multi select, built on Reka ToggleGroup. A selection control
(segmented-toggle style), distinct from ZonePill, a single display chip. For an actions menu use
ZoneButtonDropdown; for a dropdown value picker use ZoneSelect.

## v-model

Single mode: the selected id (`string | number`), or `null` when nothing is selected; clicking the
selected pill again deselects it (Reka ToggleGroup behaviour). With `multiple`: an array of selected
ids.

## Notes

- Icons scale with the pill: the leading icon and the ✓ are 12px at `sm`, 18px at `md`/`lg` (an 18px
  glyph overflows the 16px-tall sm pill).
- The three visual states come from Reka data attributes, not props: unselected is
  gray-50/gray-300/gray-500, hover adds gray-100 (only while `data-[state=off]`, never on a disabled
  pill), pressed is `data-[state=on]` primary-50/primary-700. There is no per-pill colour prop.
- Figma draws no icon on the Small selectable pill; this component allows one (12px) at every size,
  matching ZonePill.

Figma: [Selectable](https://www.figma.com/design/DD0eEumGwzGfrPbb7q2iak/-Zone--Tokens---Components?node-id=2200-2513),
[Guidelines](https://www.figma.com/design/eOrg3dHaQFZ7VdCZCcomNC/-Zone-DS--Components-Specs?node-id=1522-27704)
</docs>

<template>
  <ToggleGroupRoot
    :type="multiple ? 'multiple' : 'single'"
    :model-value="modelValue"
    :disabled="disabled"
    :name="name"
    :required="required"
    class="flex flex-wrap gap-2"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <ToggleGroupItem
      v-for="item in items"
      :key="item.id"
      :value="item.id"
      :disabled="item.disabled"
      :class="itemClasses(item)"
    >
      <template #default="{ pressed }">
        <ZoneIcon
          v-if="item.icon"
          :icon="item.icon"
          :color="pressed ? 'primary' : 'gray'"
          :shade="pressed ? 700 : 500"
          :size="sizeClasses.iconSize"
          :class="sizeClasses.leadingIcon"
        />
        <span :class="[sizeClasses.label, showsCheckmark(pressed) ? '-mr-1' : '']">{{ item.label }}</span>
        <ZoneIcon
          v-if="showsCheckmark(pressed)"
          icon="check"
          color="primary"
          :shade="700"
          :size="sizeClasses.iconSize"
          :class="sizeClasses.trailingIcon"
        />
      </template>
    </ToggleGroupItem>
  </ToggleGroupRoot>
</template>

<script lang="ts">
import { computed, type PropType } from 'vue';
import { ToggleGroupRoot, ToggleGroupItem } from 'reka-ui';
import ZoneIcon, { type IconName } from './Icon.vue';

const SIZES = ['sm', 'md', 'lg'] as const;

export type PillGroupSize = (typeof SIZES)[number];

export interface PillGroupItem {
  id: string | number;
  label: string;
  icon?: IconName;
  disabled?: boolean;
}

export type PillGroupModelValue = string | number | (string | number)[] | null;

interface PillGroupSizeClasses {
  wrapper: string;
  text: string;
  label: string;
  leadingIcon: string;
  trailingIcon: string;
  iconSize: 'xs' | 'lg';
}

// iconSize tracks the glyph Figma uses per size — 12px at Small, 18px at Medium and Large — for
// both the leading icon and the multi-select checkmark. An 18px glyph would overflow the 16px-tall
// small pill.
const SIZE_CLASSES: Record<PillGroupSize, PillGroupSizeClasses> = {
  sm: { wrapper: 'py-0', text: 'text-xs leading-4', label: 'px-2', leadingIcon: 'pl-1.5', trailingIcon: 'pr-1', iconSize: 'xs' },
  md: { wrapper: 'py-1', text: 'text-xs leading-4', label: 'px-2', leadingIcon: 'pl-1.5', trailingIcon: 'pr-1.5', iconSize: 'lg' },
  lg: { wrapper: 'py-2', text: 'text-sm leading-5', label: 'px-3', leadingIcon: 'pl-2', trailingIcon: 'pr-2', iconSize: 'lg' },
};
</script>

<script setup lang="ts">
defineOptions({ name: 'ZonePillGroup' });

const props = defineProps({
  /** The selected id (or null), or an array of ids with `multiple`. */
  modelValue: {
    type: [String, Number, Array] as PropType<PillGroupModelValue>,
    default: null,
  },
  /** The pills: `{ id, label, icon?, disabled? }`; `icon` is a Zone icon name. No slots. */
  items: {
    type: Array as PropType<PillGroupItem[]>,
    default: () => [],
  },
  /** Multi-select: the v-model becomes an array. */
  multiple: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String as PropType<PillGroupSize>,
    default: 'sm',
    validator: (v: string) => SIZES.includes(v as PillGroupSize),
  },
  /** Show a ✓ on pressed pills, multi mode only. */
  showCheckmark: {
    type: Boolean,
    default: true,
  },
  /** Native form name, so the selection submits with a form. */
  name: {
    type: String,
    default: undefined,
  },
  /** Native `required`. */
  required: {
    type: Boolean,
    default: false,
  },
});

defineEmits<{
  /** The new selection: an id or null, an array with `multiple`. */
  'update:modelValue': [value: PillGroupModelValue];
}>();

const sizeClasses = computed<PillGroupSizeClasses>(() => SIZE_CLASSES[props.size]);

// Per item rather than a shared computed, so the hover fill can be dropped entirely on a
// disabled pill — the same shape DayPicker and SegmentedControl use for their Reka items.
function itemClasses(item: PillGroupItem): string {
  const classes = [
    'inline-flex items-center rounded-full border border-solid select-none outline-none transition-colors',
    'bg-gray-50 border-gray-300 text-gray-500',
    'data-[state=on]:bg-primary-50 data-[state=on]:border-primary-700 data-[state=on]:text-primary-700',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
    sizeClasses.value.wrapper,
    sizeClasses.value.text,
  ];

  if (props.disabled || item.disabled) {
    classes.push('opacity-50 cursor-not-allowed');
  } else {
    // Figma draws Hover on the unselected pill only; a selected pill keeps its primary fill.
    classes.push('cursor-pointer data-[state=off]:hover:bg-gray-100');
  }

  return classes.join(' ');
}

// Reka exposes `pressed` only as a slot prop, so this stays a function rather than a computed.
function showsCheckmark(pressed: boolean): boolean {
  return props.multiple && props.showCheckmark && pressed;
}
</script>
